<script setup>
import { computed, onMounted, ref } from 'vue'
import { getI18n, translateOrDefault } from '@/plugins/i18n'

import { accessLevelEnum, infoBoxIdEnum, systemRoleEnum, usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import InfoBox from '@/components/shared/InfoBox.vue'

const AI_ROLE_ICON = 'mdi-robot-outline'

const i18n = getI18n()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()

const matrix = ref(null)
const permissionChanges = ref({})
const showResetConfirm = ref(false)
const isSaving = ref(false)
const roleNameDialog = ref({ show: false, role: null, name: '' })
const roleToDelete = ref(null)
const showDeleteConfirm = ref(false)

const canEdit = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS))
const hasChanges = computed(() => Object.keys(permissionChanges.value).length > 0)

const roles = computed(() => (matrix.value?.roles ?? [])
  .map(role => ({
    ...webSiteStore.getLocalizedRole(role),
    isAdministrator: role.system_role === systemRoleEnum.ADMINISTRATOR,
    isAssistant: role.system_role === systemRoleEnum.AI_ASSISTANT,
  }))
  .sort((a, b) => Number(a.isAssistant) - Number(b.isAssistant)))
const levelById = computed(() => Object.fromEntries(portalStore.getLocalizedAccessLevels().map(level => [level.id, level])))
const legendLevels = computed(() => [accessLevelEnum.FULL, accessLevelEnum.VIEW].map(id => levelById.value[id]))

const translate = (key) => translateOrDefault(key, null)
const permissionsOf = (role) => permissionChanges.value[role.id] ?? role.permissions
const levelIn = (feature, permissions) => {
  if (feature.full && permissions & feature.full) return accessLevelEnum.FULL
  if (feature.view && permissions & feature.view) return accessLevelEnum.VIEW
  return accessLevelEnum.NONE
}
const levelOf = (feature, role) => levelIn(feature, permissionsOf(role))
const allowedLevels = (feature) => [
  accessLevelEnum.NONE,
  ...(feature.view ? [accessLevelEnum.VIEW] : []),
  ...(feature.full ? [accessLevelEnum.FULL] : []),
]
const isApplicable = (feature, role) => !role.isAssistant || feature.ai_applicable
const isEditable = (feature, role) => canEdit.value && !role.isAdministrator && isApplicable(feature, role)
const isCustom = (feature, role) => {
  const defaults = matrix.value.defaults[role.system_role]
  return defaults !== undefined && levelOf(feature, role) !== levelIn(feature, defaults)
}

const withoutKey = (object, key) => Object.fromEntries(Object.entries(object).filter(([name]) => name !== String(key)))

const setLevel = (feature, role, level) => {
  const cleared = permissionsOf(role) & ~((feature.view ?? 0) | (feature.full ?? 0))
  const viewBit = level !== accessLevelEnum.NONE && feature.view ? feature.view : 0
  const fullBit = level === accessLevelEnum.FULL ? feature.full : 0
  const permissions = cleared | viewBit | fullBit
  const others = withoutKey(permissionChanges.value, role.id)
  permissionChanges.value = permissions === role.permissions ? others : { ...others, [role.id]: permissions }
}

const loadAsync = async() => {
  matrix.value = await webSiteStore.fetchPermissionMatrixAsync()
}

const reloadRolesAsync = async() => {
  await Promise.all([loadAsync(), webSiteStore.fetchRolesAsync()])
}

const saveAsync = async() => {
  isSaving.value = true
  try {
    const changes = Object.entries(permissionChanges.value).map(([role, permissions]) => ({ role: Number(role), permissions }))
    matrix.value = await webSiteStore.updatePermissionsAsync(changes)
    permissionChanges.value = {}
  } finally {
    isSaving.value = false
  }
}

const resetAsync = async() => {
  matrix.value = await webSiteStore.resetPermissionsAsync()
  permissionChanges.value = {}
}

const openRoleNameDialog = (role = null) => {
  roleNameDialog.value = { show: true, role, name: role?.name ?? '' }
}

const onRoleNameConfirmAsync = async() => {
  const { role, name } = roleNameDialog.value
  if (role) {
    await webSiteStore.updateRoleAsync(role.id, { name })
  } else {
    await webSiteStore.createRoleAsync({ name, permissions: matrix.value.defaults[systemRoleEnum.USER] })
  }
  await reloadRolesAsync()
}

const openDeleteConfirm = (role) => {
  roleToDelete.value = role
  showDeleteConfirm.value = true
}

const onRoleDeleteConfirmAsync = async() => {
  await webSiteStore.deleteRoleAsync(roleToDelete.value.id)
  permissionChanges.value = withoutKey(permissionChanges.value, roleToDelete.value.id)
  await reloadRolesAsync()
}

onMounted(loadAsync)
</script>

<template>
  <div v-if="matrix" class="permissions-matrix">
    <div v-if="canEdit" class="row justify-end q-gutter-sm">
      <q-btn
        flat
        icon="mdi-account-multiple-plus-outline"
        :label="i18n.t('permissions.addRole')"
        @click="openRoleNameDialog()"
      />
      <q-btn
        flat
        icon="mdi-restore"
        :label="i18n.t('permissions.resetDefaults')"
        @click="showResetConfirm = true"
      />
      <q-btn
        v-if="hasChanges"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isSaving"
        @click="saveAsync"
      />
    </div>
    <div class="matrix-table-scroll">
      <table class="matrix-table permissions-table">
        <thead>
          <tr>
            <th class="label-col">{{ i18n.t('common.capability', 1) }}</th>
            <th v-for="role in roles" :key="role.id" :class="[`text-${role.color}`, { 'ai-col': role.isAssistant }]">
              <q-icon
                v-if="role.isAssistant"
                :name="AI_ROLE_ICON"
                size="xs"
                class="q-mr-xs"
              />{{ role.label }}
              <q-btn
                v-if="canEdit && role.system_role === null"
                flat
                round
                dense
                size="sm"
                icon="mdi-dots-vertical"
              >
                <q-menu auto-close>
                  <q-list dense>
                    <q-item clickable @click="openRoleNameDialog(role)">
                      <q-item-section avatar><q-icon name="mdi-pencil-outline" /></q-item-section>
                      <q-item-section>{{ i18n.t('permissions.renameRole') }}</q-item-section>
                    </q-item>
                    <q-item clickable :disable="role.user_count > 0" @click="openDeleteConfirm(role)">
                      <q-item-section avatar><q-icon name="mdi-delete-outline" color="negative" /></q-item-section>
                      <q-item-section>
                        <q-item-label>{{ i18n.t('permissions.deleteRole') }}</q-item-label>
                        <q-item-label v-if="role.user_count > 0" caption>{{ i18n.t('permissions.roleInUse', { count: role.user_count }) }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in matrix.groups" :key="group.group">
            <tr class="group-row">
              <th class="label-col dialog-card-section-title">{{ i18n.t(group.label_key) }}</th>
              <td v-for="role in roles" :key="role.id" :class="{ 'ai-col': role.isAssistant }" />
            </tr>
            <tr v-for="feature in group.features" :key="feature.key" class="data-row">
              <th class="label-col">
                <span>{{ i18n.t(feature.label_key) }}</span>
                <span v-if="translate(feature.hint_key)" class="label-sub">{{ translate(feature.hint_key) }}</span>
              </th>
              <td v-for="role in roles" :key="role.id" :class="{ 'ai-col': role.isAssistant }">
                <template v-if="isApplicable(feature, role)">
                  <q-chip
                    v-if="levelOf(feature, role) !== accessLevelEnum.NONE"
                    size="sm"
                    :clickable="isEditable(feature, role)"
                    :outline="isCustom(feature, role)"
                    :class="{ 'text-weight-bold': isCustom(feature, role) }"
                    :color="levelById[levelOf(feature, role)].color"
                    :text-color="isCustom(feature, role) ? undefined : levelById[levelOf(feature, role)].textColor"
                    :icon="role.isAdministrator ? 'mdi-lock-outline' : levelById[levelOf(feature, role)].icon"
                    :label="levelById[levelOf(feature, role)].label"
                  >
                    <q-tooltip v-if="role.isAdministrator">{{ i18n.t('permissions.locked') }}</q-tooltip>
                  </q-chip>
                  <q-btn
                    v-else-if="isEditable(feature, role)"
                    flat
                    dense
                    :class="['matrix-cell-none', { 'text-weight-bold': isCustom(feature, role) }]"
                    label="—"
                  />
                  <span v-else class="matrix-cell-none">—</span>
                  <q-menu v-if="isEditable(feature, role)" auto-close>
                    <q-list dense>
                      <q-item
                        v-for="level in allowedLevels(feature)"
                        :key="level"
                        clickable
                        :active="level === levelOf(feature, role)"
                        @click="setLevel(feature, role, level)"
                      >
                        <q-item-section avatar>
                          <q-icon :name="levelById[level].icon ?? 'mdi-minus'" :color="levelById[level].color" />
                        </q-item-section>
                        <q-item-section>{{ levelById[level].label }}</q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </template>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <info-box :id="infoBoxIdEnum.PERMISSIONS_NOTE">
      <div>{{ i18n.t('permissions.matrixNote') }}</div>
      <div class="key q-mt-sm">
        <span v-for="level in legendLevels" :key="level.id" class="key-item">
          <q-chip
            size="sm"
            :color="level.color"
            :text-color="level.textColor"
            :icon="level.icon"
            :label="level.label"
          />
          <span class="text-caption text-grey-7">{{ level.hint }}</span>
        </span>
        <span class="key-item">
          <span class="matrix-cell-none">—</span>
          <span class="text-caption text-grey-7">{{ levelById[accessLevelEnum.NONE]?.label }}</span>
        </span>
      </div>
    </info-box>
    <confirm-dialog
      v-model="showResetConfirm"
      icon="mdi-restore"
      :message="i18n.t('permissions.resetDefaultsConfirm')"
      @confirm="resetAsync"
    />
    <confirm-dialog
      v-model="roleNameDialog.show"
      icon="mdi-account-multiple-outline"
      :title="i18n.t(roleNameDialog.role ? 'permissions.renameRole' : 'permissions.addRole')"
      message=""
      @confirm="onRoleNameConfirmAsync"
    >
      <q-input
        v-model="roleNameDialog.name"
        autofocus
        :label="i18n.t('permissions.roleName')"
        :rules="[val => !!(val && val.trim()) || i18n.t('common.required')]"
      />
    </confirm-dialog>
    <confirm-dialog
      v-model="showDeleteConfirm"
      icon="mdi-delete-outline"
      icon-color="negative"
      :message="i18n.t('permissions.deleteRoleConfirm', { name: roleToDelete?.label })"
      @confirm="onRoleDeleteConfirmAsync"
    />
  </div>
</template>

<style lang="scss" scoped>
.permissions-matrix {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
}

.permissions-table {
  min-width: 50rem;

  .ai-col {
    background: rgba($deep-purple, 0.04);
    border-left: 1px solid rgba($deep-purple, 0.15);
  }
}

.key {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 1.25rem;
}

.key-item {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
}
</style>
